<!-- Ein Diskantknopf: Beschriftung, Aktiv-/Vorschau-Zustand, Gleichton-Hinweise. Emits activate/deactivate; mit `playable` (Default) klingt er, solange er gedrückt ist. -->
<script setup lang="ts">
import { computed, onBeforeUnmount } from 'vue'

import { startButtonTones } from './buttonTone'
import { buttonMidis } from './instrumentData'
import type { BellowsDirection, PrecalcButton } from './harmonika.types'

type ButtonVariant = 'inspector' | 'simulator'

const {
  rowIdx,
  btnIdx,
  btnData,
  variant = 'inspector',
  isActive = false,
  isPreview = false,
  label = ' ',
  keyLabel = '',
  activeMidiNotes,
  playable = true,
  direction = 'DRUCK',
} = defineProps<{
  rowIdx: number
  btnIdx: number
  btnData: PrecalcButton
  variant?: ButtonVariant
  isActive?: boolean
  isPreview?: boolean
  label?: string
  keyLabel?: string
  activeMidiNotes: Set<number>
  /** Klingt, solange er gedrückt ist. Aus, wo der Aufrufer selbst spielt (Simulator). */
  playable?: boolean
  /** Balgrichtung, deren Töne `playable` spielt. */
  direction?: BellowsDirection
}>()

const emit = defineEmits<{
  activate: [rowIdx: number, btnIdx: number]
  deactivate: [rowIdx: number, btnIdx: number]
}>()

const isOrientationTone = computed(
  () => (rowIdx !== 0 && btnIdx === 5) || (rowIdx === 0 && btnIdx === 6),
)

const hasLabel = computed(() => label.trim().length > 0)
const hasKeyLabel = computed(() => keyLabel.trim().length > 0)

// In der Simulator-Ansicht besitzt die Vorschau bewusst keinen eigenen Zustand.
const showPreview = computed(() => variant === 'inspector' && isPreview)
const showActive = computed(() => isActive && !showPreview.value)

const showHints = computed(() => activeMidiNotes.size > 0 && !isActive)
const matchesDruck = computed(
  () => showHints.value && btnData.druckMidis.some((midi) => activeMidiNotes.has(midi)),
)
const matchesZug = computed(
  () => showHints.value && btnData.zugMidis.some((midi) => activeMidiNotes.has(midi)),
)

const ariaLabel = computed(() => {
  const description = [
    `Reihe ${rowIdx + 1}, Knopf ${btnIdx + 1}`,
    `Druck ${btnData.druck.join(' / ')}`,
    `Zug ${btnData.zug.join(' / ')}`,
  ]

  if (matchesDruck.value) description.push('Gleicher Ton auf Druck')
  if (matchesZug.value) description.push('Gleicher Ton auf Zug')

  return description.join(', ')
})

// Pointer und Tastatur werden separat erfasst, damit ein Ton erst dann endet,
// wenn die letzte aktive Eingabe losgelassen wird.
const heldInputs = new Set<string>()
let stopTones: (() => void) | null = null

function release(): void {
  stopTones?.()
  stopTones = null
  emit('deactivate', rowIdx, btnIdx)
}

function startInput(inputId: string): void {
  if (heldInputs.has(inputId)) return

  const wasIdle = heldInputs.size === 0
  heldInputs.add(inputId)

  if (!wasIdle) return
  if (playable) stopTones = startButtonTones(buttonMidis(btnData, direction))
  emit('activate', rowIdx, btnIdx)
}

function stopInput(inputId: string): void {
  if (!heldInputs.delete(inputId)) return
  if (heldInputs.size === 0) release()
}

function stopAllInputs(): void {
  if (heldInputs.size === 0) return

  heldInputs.clear()
  release()
}

function handlePointerDown(event: PointerEvent): void {
  if (event.button !== 0) return

  startInput(`pointer:${event.pointerId}`)

  // Capture nur für Touch/Pen. Bei der Maus soll ein Verlassen des
  // Knopfes die Note wie bei einer echten Taste sofort beenden.
  if (event.pointerType === 'mouse') return

  const target = event.currentTarget
  if (target instanceof HTMLButtonElement && target.isConnected) {
    target.setPointerCapture(event.pointerId)
  }
}

function handlePointerEnd(event: PointerEvent): void {
  stopInput(`pointer:${event.pointerId}`)
}

function handlePointerLeave(event: PointerEvent): void {
  if (event.pointerType === 'mouse') handlePointerEnd(event)
}

function keyboardInputId(event: KeyboardEvent): string | null {
  if (event.key === 'Enter') return 'keyboard:enter'
  if (event.key === ' ') return 'keyboard:space'
  return null
}

function handleKeyDown(event: KeyboardEvent): void {
  const inputId = keyboardInputId(event)
  if (!inputId) return

  event.preventDefault()
  if (!event.repeat) startInput(inputId)
}

function handleKeyUp(event: KeyboardEvent): void {
  const inputId = keyboardInputId(event)
  if (!inputId) return

  event.preventDefault()
  stopInput(inputId)
}

onBeforeUnmount(stopAllInputs)
</script>

<template>
  <button
    type="button"
    class="harmonika-button"
    :class="[
      `harmonika-button--${variant}`,
      {
        'is-active': showActive,
        'is-preview': showPreview,
        'is-orientation': isOrientationTone,
      },
    ]"
    :aria-label="ariaLabel"
    :aria-pressed="isActive"
    :title="ariaLabel"
    @pointerdown="handlePointerDown"
    @pointerup="handlePointerEnd"
    @pointerleave="handlePointerLeave"
    @pointercancel="handlePointerEnd"
    @lostpointercapture="handlePointerEnd"
    @keydown="handleKeyDown"
    @keyup="handleKeyUp"
    @blur="stopAllInputs"
    @contextmenu.prevent
    @dragstart.prevent
  >
    <span
      v-if="matchesDruck"
      class="harmonika-button__hint harmonika-button__hint--druck"
      aria-hidden="true"
    />
    <span
      v-if="matchesZug"
      class="harmonika-button__hint harmonika-button__hint--zug"
      aria-hidden="true"
    />

    <span
      class="harmonika-button__face"
      :class="{ 'harmonika-button__face--dual': hasKeyLabel && hasLabel }"
    >
      <span v-if="hasKeyLabel" class="harmonika-button__key-label">
        {{ keyLabel }}
      </span>
      <span v-if="hasLabel" class="harmonika-button__label">
        {{ label }}
      </span>
      <span v-else class="harmonika-button__number" aria-hidden="true">
        {{ btnIdx + 1 }}
      </span>
    </span>
  </button>
</template>

<style scoped>
/* Modern Studio: weiche Keramikkappen, präzise Metallkanten, klare Zustände. */
.harmonika-button {
  --cap-light: #ffffff;
  --cap-middle: #f3f6fa;
  --cap-dark: #dfe6ee;
  --cap-outline: #bac7d7;
  --cap-foot: #9cacc0;
  --cap-ink: #344256;
  --cap-shadow: rgb(40 58 78 / 15%);

  position: relative;
  z-index: 0;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: var(--button-size, 2rem);
  height: var(--button-size, 2rem);
  padding: 1px;
  border: 1px solid var(--cap-outline);
  border-radius: 50%;
  background: linear-gradient(150deg, var(--cap-light) 4%, var(--cap-middle) 50%, var(--cap-dark) 100%);
  box-shadow:
    inset 0 1px 1px rgb(255 255 255 / 85%),
    inset 0 -2px 3px rgb(52 71 92 / 13%),
    0 2px 0 var(--cap-foot),
    0 5px 8px var(--cap-shadow);
  color: var(--cap-ink);
  cursor: pointer;
  font-family: inherit;
  text-align: center;
  touch-action: pan-x;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition:
    background 140ms ease,
    transform 140ms ease,
    box-shadow 140ms ease,
    filter 140ms ease;
}

.harmonika-button::before {
  position: absolute;
  inset: 10%;
  border: 1px solid rgb(255 255 255 / 50%);
  border-radius: 50%;
  content: '';
  pointer-events: none;
}

/* Hochkant muss das Board vertikal gescrollt werden können. */
:global(.treble-board--vertical) .harmonika-button {
  touch-action: pan-y;
}

.harmonika-button--simulator {
  --cap-light: #ffffff;
  --cap-middle: #e8eef5;
  --cap-dark: #cdd7e4;
  --cap-outline: #9eafc3;
  --cap-foot: #7b8ca1;
  --cap-ink: #253247;
  --cap-shadow: rgb(1 9 24 / 52%);
}

/* Aktivzustände sind strikt nach Variante getrennt. */
.harmonika-button--inspector.is-active {
  --cap-light: #b5dcff;
  --cap-middle: #4a9aef;
  --cap-dark: #226aca;
  --cap-outline: #2e76c9;
  --cap-foot: #174e9c;
  --cap-ink: #ffffff;
}

.harmonika-button--simulator.is-active {
  --cap-light: #ffb4bc;
  --cap-middle: #f56b78;
  --cap-dark: #cd334f;
  --cap-outline: #e1536a;
  --cap-foot: #95273f;
  --cap-ink: #ffffff;
}

.harmonika-button--inspector.is-preview {
  --cap-light: #fff0c7;
  --cap-middle: #ffd173;
  --cap-dark: #e9a53d;
  --cap-outline: #d99e48;
  --cap-foot: #aa772f;
  --cap-ink: #633f15;
}

.harmonika-button.is-active {
  z-index: 1;
  transform: translateY(2px);
  box-shadow:
    inset 0 2px 5px rgb(0 0 0 / 14%),
    inset 0 1px 1px rgb(255 255 255 / 35%),
    0 0 0 1px var(--cap-outline),
    0 1px 3px var(--cap-shadow);
}

.harmonika-button.is-orientation::after {
  position: absolute;
  inset: -4px;
  border: 2px solid #15a6a6;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgb(20 166 166 / 10%);
  content: '';
  pointer-events: none;
}

.harmonika-button--simulator.is-orientation::after {
  border-color: #60d7d6;
  box-shadow: 0 0 0 2px rgb(96 215 214 / 16%);
}

.harmonika-button:focus-visible {
  z-index: 4;
  outline: 3px solid #38bdf8;
  outline-offset: 5px;
}

.harmonika-button__face {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  width: 100%;
  height: 100%;
  max-width: 100%;
  overflow: hidden;
  border-radius: 50%;
  pointer-events: none;
}

.harmonika-button__key-label {
  max-width: 90%;
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: clamp(0.42rem, calc(var(--button-size) * 0.17), 0.57rem);
  font-weight: 650;
  line-height: 1;
  opacity: 0.72;
  text-transform: uppercase;
}

.harmonika-button__label {
  max-width: 92%;
  font-size: clamp(0.59rem, calc(var(--button-size) * 0.29), 0.91rem);
  font-weight: 750;
  letter-spacing: -0.03em;
  line-height: 1.04;
  overflow-wrap: anywhere;
  white-space: pre-line;
}

.harmonika-button__face--dual .harmonika-button__label {
  font-size: clamp(0.54rem, calc(var(--button-size) * 0.255), 0.79rem);
}

.harmonika-button__number {
  font-size: clamp(0.54rem, calc(var(--button-size) * 0.26), 0.8rem);
  font-weight: 650;
  opacity: 0.36;
  transition: opacity 140ms ease;
}

.harmonika-button__hint {
  position: absolute;
  z-index: 3;
  top: -4px;
  width: clamp(9px, calc(var(--button-size) * 0.27), 13px);
  height: clamp(9px, calc(var(--button-size) * 0.27), 13px);
  border: 2px solid #f8fafc;
  border-radius: 50%;
  pointer-events: none;
}

.harmonika-button__hint--druck {
  left: -4px;
  background: #10b981;
  box-shadow: 0 0 0 2px rgb(16 185 129 / 15%), 0 0 9px rgb(16 185 129 / 52%);
}

.harmonika-button__hint--zug {
  right: -4px;
  background: #4096f0;
  box-shadow: 0 0 0 2px rgb(64 150 240 / 15%), 0 0 9px rgb(64 150 240 / 52%);
}

@media (hover: hover) {
  .harmonika-button:not(.is-active):hover {
    z-index: 2;
    filter: brightness(1.05);
    transform: translateY(-1px);
  }

  .harmonika-button:hover .harmonika-button__number {
    opacity: 0.8;
  }
}

@media (prefers-reduced-motion: reduce) {
  .harmonika-button,
  .harmonika-button__number {
    transition: none;
  }
}
</style>
